import { computed, reactive, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useQuoteDetailPage } from './useQuoteDetailPage.js'
import { usePreviewQuoteDetail } from './usePreviewQuoteDetail.js'
import { useBesoinSectionsConfirm } from './useBesoinSectionsConfirm.js'
import { useBesoinMissing } from './useBesoinMissing.js'
import { useBesoinConfirmGuard } from './useBesoinConfirmGuard.js'
import { useBesoinEmailConfirm } from './useBesoinEmailConfirm.js'
import { useBesoinPmrParDefaut } from './useBesoinPmrParDefaut.js'
import { useQualificationAnswers, questionnairesDeLaPartie } from '../questionnaires/useQualificationAnswers.js'
import { questionnaireDeplie } from '../../../shared/blocs-communs.js'
import { groupesNecessaires, totalDesGroupes, formulesDuDevis } from '../../utils/formuleFormation.js'
import { enHtEtTtc } from '../../utils/tarifHtTtc.js'
import { getQuoteAmounts } from '../../utils/alertis-siret.js'
import { tvaRateCertain } from '@shared/alertis-siret.js'
import { useGlobalCall } from '../calls/useGlobalCall.js'
import { quoteValidationService } from '../../services/quoteValidationService.js'
import { questionnairesService } from '../../services/questionnairesService.js'
import {
  BESOIN_COMMUN_SECTIONS,
  createEmptyBesoinCommun
} from '../questionnaires/besoin-commun-schema.js'

/**
 * ÉTAT DE LA FENÊTRE « ANALYSE DU BESOIN » — nouvelle interface.
 *
 * Le commercial remplit ce questionnaire PENDANT son appel : il ouvre la
 * fenêtre depuis le bandeau d'appel, confirme les sections une à une, puis
 * enregistre — ce qui clôt l'appel et enchaîne sur l'éditeur de devis.
 *
 * Ce composable porte tout ce qui n'est pas de la mise en page : le chargement
 * de la demande et de sa validation, les réponses, les sections confirmées, ce
 * qui manque, et l'enregistrement. `QuoteBesoinDialog.vue` n'en fait que le
 * rendu.
 *
 * L'ÉCHEC D'ENREGISTREMENT NE PART PAS EN TOAST (M9) : il est exposé dans
 * `error` et la fenêtre l'affiche en tête de corps, la saisie intacte. Un toast
 * s'efface au bout de quelques secondes alors que la fenêtre, elle, est encore
 * ouverte et le problème encore là.
 *
 * @param {string|number} quoteId - Identifiant de la demande. VALEUR BRUTE et
 *   non ref : `useQuoteDetailPage` la consomme telle quelle (`quotesService
 *   .detail(quoteId)`), un ref y arriverait sous la forme `[object Object]`.
 *   La fenêtre change de demande en se remontant (`:key` sur l'hôte).
 * @param {Object} [options]
 * @param {boolean} [options.preview] - Aperçu : rien n'est enregistré.
 */
export function useQuoteBesoinForm(quoteId, options = {}) {
  const preview = options.preview === true
  const router = useRouter()

  // Un appel peut être en cours (bouton « Analyse du besoin » du bandeau) :
  // enregistrer l'analyse conclut alors l'appel abouti.
  const { endCallFromBesoin } = useGlobalCall()

  // En aperçu, aucune demande réelle : on substitue un jeu d'exemple qui expose
  // la même surface. `preview` est constant pour l'instance → branche sûre.
  const quoteDetail = preview ? usePreviewQuoteDetail() : useQuoteDetailPage(quoteId)
  const {
    quote, loading, isEditing, editedQuote, saving, canEdit, isQuoteSigned,
    startEditing, saveQuote, getValue,
    loadingFormations, categories, availableFormations, selectedFormation, formationLieuOptions,
    availableVilles, availableSessions, selectedSession, requiresExteriorSpaceForFormation,
    onCategoryChange, onFormationChange, onLieuChange, onVilleChange,
    handleDatesDisponibilitesChange, handleDatesPeriodesChange, handleDatesInconnuesChange
  } = quoteDetail

  // Champs propres à l'analyse du besoin (persistés dans QuoteValidation).
  //
  // Le nombre de groupes n'est plus saisi ici : le bloc Formation le porte, sur
  // `nombre_groupes`, et c'est lui qui décide du tarif. L'analyse recopie ce
  // qu'a retenu la demande plutôt que d'en tenir une seconde version.
  const nombreGroupes = computed(() => (
    editedQuote.value?.nombre_groupes ?? quote.value?.nombre_groupes ?? groupesConseilles.value
  ))
  const vigilanceComment = ref('')
  const savingBesoin = ref(false)
  const error = ref(null)

  // Sections communes avec le questionnaire d'organisation de la formation
  // (étape 2) : mêmes champs, mêmes options, mêmes obligations.
  const besoinQuestionnaire = reactive(createEmptyBesoinCommun())
  const { poserPmrParDefaut, pmrAEnregistrer } = useBesoinPmrParDefaut({ besoinQuestionnaire, quote, isQuoteSigned })

  // Questionnaires de qualification rattachés à la formation. Le questionnaire
  // « vigilance » est exclu : le bilan est calculé, pas saisi.
  const specificQuestionnaires = ref([])
  const hasSpecificQuestionnaires = computed(() => specificQuestionnaires.value.length > 0)
  const {
    answers, getAnswer, setAnswers, handleUpdateAnswer, handleUpdateComment,
    sortedQuestions, questionsPosees, buildAnswersPayload
  } = useQualificationAnswers(specificQuestionnaires)

  /** La réponse affichée a-t-elle été corrigée par le client ? */
  const isAnswerFromClient = (questionId) => answers.value[questionId]?.updatedBySource === 'client'

  // « Adresse e-mail confirmée avec le client » : obligatoire pour confirmer
  // « Informations personnelles ».
  const {
    isEmailConfirmed, canConfirmEmail, setEmailConfirmed, restoreEmailConfirmed,
    emailConfirmePayload, emailMissing
  } = useBesoinEmailConfirm({ quote, getValue })

  // Ce qui manque, section par section : une seule liste pour le pied, le
  // plan, le bouton final et le refus de confirmer une section.
  const { missingFields, missingBySection, errorsFor } = useBesoinMissing({
    quote, besoinQuestionnaire, specificQuestionnaires, answers, questionsPosees, nombreGroupes,
    extraMissing: [emailMissing]
  })

  const {
    sectionsConfirmed, sectionLocked, sectionEditing,
    unconfirmedSections, allSectionsConfirmed, sectionLabel, restoreSections
  } = useBesoinSectionsConfirm({ quote, isQuoteSigned, isEditing, getValue })

  const {
    confirmAttempted, droppedNotice, confirmSection, reopenSection, refusedCount
  } = useBesoinConfirmGuard({ sectionsConfirmed, missingBySection, isQuoteSigned, sectionLabel })

  /** Erreurs à montrer dans une section : seulement après un refus de la confirmer. */
  const sectionErrors = (key) => (confirmAttempted[key] ? errorsFor(key) : {})

  /**
   * La formule retenue sur la demande, quand il y en a une : sa fourchette haute
   * est la taille d'un groupe.
   */
  const formuleRetenue = computed(() => {
    const id = Number(
      editedQuote.value?.formation_formule_id ?? quote.value?.formation_formule_id
    )
    return formulesDuDevis(selectedFormation.value, quote.value).find((f) => f.id === id) || null
  })

  /**
   * Le nombre de groupes qu'appelle l'effectif annoncé — même règle que le
   * formulaire public : vingt personnes en groupes de douze, c'est deux groupes.
   *
   * Sans formule retenue la taille d'un groupe est inconnue : `groupesNecessaires`
   * retombe alors sur 1, l'ancienne valeur par défaut.
   */
  const groupesConseilles = computed(() => groupesNecessaires(
    editedQuote.value?.intra_nombre_participants ?? quote.value?.intra_nombre_participants,
    formuleRetenue.value?.participantsMax
  ))

  /**
   * Ce que vaut la demande telle qu'elle est retenue : le prix de la formule,
   * qui est un prix PAR GROUPE, multiplié par le nombre de groupes.
   *
   * L'analyse du besoin est le moment où le commercial arrête l'effectif et le
   * découpage : il doit voir ce que ça chiffre sans changer d'écran.
   */
  // Le taux choisi dans l'éditeur du devis prime ; sans activité connue d'une
  // entreprise assujettie, il reste incertain et seul le HT s'affiche.
  const tauxTva = computed(() => {
    const demande = { ...quote.value, ...editedQuote.value }
    return tvaRateCertain(demande) === null ? null : getQuoteAmounts(demande).tvaRate
  })

  const tarifEstime = computed(() => {
    const total = totalDesGroupes(formuleRetenue.value, nombreGroupes.value)
    return total === null ? '' : enHtEtTtc(total, tauxTva.value)
  })

  const detailTarifEstime = computed(() => {
    const groupes = Number(nombreGroupes.value)
    if (!tarifEstime.value || !(groupes > 1)) return ''
    return `Par groupe : ${enHtEtTtc(Number(formuleRetenue.value.prix), tauxTva.value)} (${groupes} groupes)`
  })

  // Vrai une fois les questionnaires lus pour la demande chargée : leur arrivée
  // peut décocher « Formation » (réponse obligatoire manquante), et l'écran
  // attend ce moment pour prendre la référence de sa garde de fermeture.
  const questionnairesCharges = ref(false)

  async function loadSpecificQuestionnaires() {
    await lireQuestionnaires()
    if (preview || quote.value) questionnairesCharges.value = true
  }

  async function lireQuestionnaires() {
    // Aperçu : pas de formation pour filtrer, on montre tous les questionnaires
    // de qualification actifs — c'est le seul endroit où les voir en contexte.
    if (preview) {
      try {
        const res = await questionnairesService.list()
        // Comme le serveur pour une vraie demande : la partie « organisation »
        // est posée au client seul, jamais au commercial.
        // Les blocs communs insérés sont dépliés, comme le serveur le fait pour
        // une vraie demande ; les blocs eux-mêmes ne sont pas des questionnaires.
        specificQuestionnaires.value = questionnairesDeLaPartie(
          (res.questionnaires || [])
            .filter((item) => !item.questionnaireType && !item.estBloc && item.code !== 'vigilance')
            .map((item) => questionnaireDeplie(item)),
          'qualification'
        ).filter((item) => item.questions.length > 0)
      } catch (e) {
        specificQuestionnaires.value = []
      }
      return
    }
    const formationNom = quote.value && getValue('formation_nom')
    const formationCategorie = quote.value && getValue('formation_categorie')
    if (!formationNom || !formationCategorie) {
      specificQuestionnaires.value = []
      return
    }
    try {
      const res = await quoteValidationService.getApplicableQuestionnaires(
        formationNom,
        formationCategorie,
        getValue('formation_catalogue_id')
      )
      specificQuestionnaires.value = (res.questionnaires || []).filter(
        (item) => item.code !== 'vigilance'
      )
    } catch (e) {
      specificQuestionnaires.value = []
    }
  }

  const isBesoinComplete = computed(() => missingFields.value.length === 0)
  // La demande doit être chargée : avant, aucune section n'existe, et « toutes
  // confirmées » est trivialement vrai — le bouton s'ouvrait pendant le chargement.
  const canValider = computed(() => Boolean(quote.value) && isBesoinComplete.value && allSectionsConfirmed.value)
  const busy = computed(() => saving.value || savingBesoin.value)

  /**
   * La phrase du pied : ce qui manque, dit AVANT le clic. La v1 réservait ce
   * message à `handleValider`, déclenché au clic — sur un bouton désactivé,
   * donc jamais atteint : le commercial voyait un bouton mort, au téléphone.
   */
  const blockingMessage = computed(() => {
    if (!quote.value) return null
    // Devis signé : le bouton d'enregistrement n'existe plus, il n'y a donc rien
    // à expliquer — réclamer des confirmations que l'écran refuse de laisser
    // cocher ne fait que désigner une porte fermée.
    if (isQuoteSigned.value) return null
    if (canValider.value) return null
    const sections = unconfirmedSections.value.length
    const champs = missingFields.value.length
    const parts = []
    if (sections) parts.push(sections > 1 ? `${sections} sections à confirmer` : '1 section à confirmer')
    if (champs) parts.push(champs > 1 ? `${champs} réponses obligatoires` : '1 réponse obligatoire')
    if (!parts.length) return null
    return `Il reste ${parts.join(' et ')}.`
  })

  const handleInseeRefresh = () => {
    if (quoteDetail.loadQuote) quoteDetail.loadQuote()
  }

  // Passe en édition dès que la demande est chargée (si autorisé).
  const stopEditWatch = watch(
    quote,
    (q) => {
      if (q && canEdit.value && !isQuoteSigned.value && !isEditing.value) {
        startEditing()
        stopEditWatch()
      }
    },
    { immediate: true }
  )

  // Recharge les questionnaires quand la formation change. Si elle a réellement
  // changé (pas l'hydratation initiale null → valeur), les questions posées dans
  // « Formation » ne sont plus les mêmes : sa confirmation, si elle existait, ne
  // vaut plus.
  // L'id catalogue fait partie de la clé : deux déclinaisons de durée partagent
  // nom et catégorie, sans lui le changement passerait inaperçu.
  const formationChanged = ref(false)
  watch(
    () => (quote.value ? [getValue('formation_nom'), getValue('formation_categorie'), getValue('formation_catalogue_id')] : []),
    ([nom, categorie, catalogueId], [oldNom, oldCategorie, oldCatalogueId] = []) => {
      loadSpecificQuestionnaires()
      if (oldNom && (nom !== oldNom || categorie !== oldCategorie || catalogueId !== oldCatalogueId) && sectionsConfirmed.formation) {
        sectionsConfirmed.formation = false
        formationChanged.value = true
      }
    }
  )

  /**
   * Un décochage automatique se DIT, sinon il passe pour un bug : changer le
   * type de client, le lieu ou la formation remplace entièrement le contenu
   * d'une section, et sa confirmation saute.
   */
  const autoUncheckNotice = ref(null)
  watch(formationChanged, (v) => {
    if (v) {
      autoUncheckNotice.value = 'La formation a changé : « Formation » a été décochée, ses questions ne sont plus les mêmes.'
      formationChanged.value = false
    }
  })
  watch(
    () => (quote.value ? getValue('client_type') : null),
    (val, old) => {
      // Seule une entreprise a une section à elle : passer en particulier
      // n'en décoche aucune, il n'y a rien à dire.
      if (old && val !== old && val === 'professionnel') {
        autoUncheckNotice.value = 'Le type de client a changé : « Informations professionnelles » a été décochée, son contenu n’est plus le même.'
      }
    }
  )
  watch(
    () => (quote.value ? getValue('formation_lieu') : null),
    (val, old) => {
      // En INTER, la session se choisit dans « Formation » : pas de section
      // de lieu, rien de décoché à annoncer.
      if (old && val !== old && (val === 'INTRA' || val === 'LOCATION')) {
        autoUncheckNotice.value = 'Le lieu de la formation a changé : « Détails du lieu de formation » a été décochée, son contenu n’est plus le même.'
      }
    }
  )
  // Section décochée d'office parce qu'une réponse obligatoire y manque.
  watch(droppedNotice, (message) => {
    if (!message) return
    autoUncheckNotice.value = message
    droppedNotice.value = null
  })
  // Tout est reconfirmé : l'avertissement a été suivi, il n'a plus rien à dire.
  watch(allSectionsConfirmed, (all) => {
    if (all && quote.value) autoUncheckNotice.value = null
  })
  const dismissAutoUncheckNotice = () => { autoUncheckNotice.value = null }

  /** Charge la validation enregistrée et les questionnaires de la formation. */
  async function load() {
    error.value = null
    autoUncheckNotice.value = null
    if (preview) {
      await loadSpecificQuestionnaires()
      return
    }
    try {
      const res = await quoteValidationService.get(quoteId)
      const validation = res.validation
      const besoin = validation?.besoin
      if (besoin) {
        // sectionsConfirmed et emailConfirme voyagent dans besoinData mais
        // n'appartiennent pas au questionnaire : on les sépare avant de
        // restaurer les réponses.
        let savedSections = null
        if (besoin.data && typeof besoin.data === 'object') {
          // `typeEtablissement` est écarté : c'est désormais une réponse au bloc
          // commun « Type d'établissement », plus une donnée de l'analyse.
          // eslint-disable-next-line no-unused-vars
          const { sectionsConfirmed: saved, emailConfirme, typeEtablissement: _ancienType, ...besoinData } = besoin.data
          savedSections = saved
          restoreEmailConfirmed(emailConfirme)
          Object.assign(besoinQuestionnaire, besoinData)
        }
        restoreSections(savedSections, besoin.infosConfirmed === true)
        vigilanceComment.value = besoin.vigilanceComment || ''
      }

      if (Array.isArray(validation?.answers)) {
        setAnswers(validation.answers)
      }
    } catch (e) {
      // Pas de validation existante : champs vides.
    }

    poserPmrParDefaut()
    await loadSpecificQuestionnaires()
  }

  /**
   * Enregistre l'analyse, clôt l'appel abouti et enchaîne sur l'éditeur.
   * @returns {Promise<boolean>} vrai si l'enregistrement a abouti.
   */
  async function submit() {
    if (preview) {
      error.value = 'Mode aperçu : rien n’est enregistré.'
      return false
    }
    if (!canValider.value) return false

    savingBesoin.value = true
    error.value = null
    try {
      // 1) Persister les modifications de la demande (source de vérité). On
      //    persiste même pour les sections verrouillées (editedQuote garde les
      //    saisies faites avant verrouillage) tant que le devis n'est pas signé.
      if (isEditing.value && !isQuoteSigned.value) {
        await saveQuote()
      }

      // 2) Persister l'analyse du besoin (snapshot QuoteValidation).
      const q = quote.value || {}
      await quoteValidationService.save(quoteId, {
        formationCategorie: q.formation_categorie,
        formationNom: q.formation_nom,
        formationLieu: q.formation_lieu,
        // Plus de `formationCommentaire` : le commentaire du besoin EST celui du
        // client, enregistré une seule fois ci-dessous en `lieuCommentaire`.
        // Les analyses antérieures gardent leur copie, que le récapitulatif
        // sait encore lire.
        lieuAdresse: q.lieu_formation_adresse || null,
        lieuCodePostal: q.lieu_formation_code_postal || null,
        lieuVille: q.lieu_formation_ville || null,
        stationnementFormateur: q.stationnement_formateur || null,
        nombreParticipants: q.intra_nombre_participants || null,
        lieuCommentaire: q.lieu_formation_commentaire || null,
        selectedDates: q.dates_disponibilites || [],
        nombreGroupes: nombreGroupes.value,
        // L'état des cases par section voyage dans besoinData (JSON libre) ; le
        // booléen global infosConfirmed reste alimenté pour compatibilité.
        besoinData: {
          ...besoinQuestionnaire,
          pmr: pmrAEnregistrer(),
          sectionsConfirmed: { ...sectionsConfirmed },
          emailConfirme: emailConfirmePayload()
        },
        vigilanceConfirmed: isBesoinComplete.value,
        vigilanceComment: vigilanceComment.value || null,
        infosConfirmed: allSectionsConfirmed.value,
        // Le backend refuse déjà de supprimer les réponses existantes sur un
        // payload vide ; on évite aussi de l'émettre quand rien n'a été chargé
        // (formation non résolue, ou erreur réseau avalée plus haut).
        answers: hasSpecificQuestionnaires.value ? buildAnswersPayload() : undefined
      })

      // 3) L'analyse du besoin enregistre l'appel, mais ne le termine plus :
      //    le commercial enchaîne sur l'édition puis l'envoi du devis, et c'est
      //    l'envoi qui clôt l'échange. Le point de vigilance saisi remonte au
      //    passage dans le fil de commentaires de la fiche, sous sa mention.
      await endCallFromBesoin(vigilanceComment.value)

      // Enchaîne sur l'édition du devis.
      router.push(`/quotes/${quoteId}?editor=1`)
      return true
    } catch (e) {
      error.value = e.message || 'L’enregistrement a échoué. Votre saisie est intacte : réessayez.'
      console.error(e)
      return false
    } finally {
      savingBesoin.value = false
    }
  }

  return {
    // Demande
    quote, loading, isEditing, editedQuote, saving, isQuoteSigned, getValue,
    loadingFormations, categories, availableFormations, selectedFormation, formationLieuOptions,
    availableVilles, availableSessions, selectedSession, requiresExteriorSpaceForFormation,
    onCategoryChange, onFormationChange, onLieuChange, onVilleChange,
    handleDatesDisponibilitesChange, handleDatesPeriodesChange, handleDatesInconnuesChange, handleInseeRefresh,
    // Champs propres au besoin
    nombreGroupes, tarifEstime, detailTarifEstime,
    besoinQuestionnaire, besoinCommunSections: BESOIN_COMMUN_SECTIONS, vigilanceComment,
    isEmailConfirmed, canConfirmEmail, setEmailConfirmed,
    // Questionnaires de qualification
    specificQuestionnaires, hasSpecificQuestionnaires,
    getAnswer, handleUpdateAnswer, handleUpdateComment, sortedQuestions, questionsPosees,
    isAnswerFromClient,
    // Confirmation par section
    sectionsConfirmed, sectionLocked, sectionEditing, unconfirmedSections, allSectionsConfirmed,
    confirmSection, reopenSection, sectionErrors, refusedCount,
    autoUncheckNotice, dismissAutoUncheckNotice,
    // Complétude et enregistrement
    missingFields, missingBySection, isBesoinComplete, canValider, busy, error, blockingMessage,
    load, submit, questionnairesCharges
  }
}
